import {
    SearchMicroViewBundleReadyChannel,
} from '@tableau/search-microview-communication-channels';
import axios from 'axios';
import * as queryString from 'query-string';

import { LocalizationLoader } from '~src/commonInfra/services/LocalizationLoader';
import { Location } from '~src/commonInfra/utils/Location';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';

import * as DataConnectionListener from '../communicationChannelListeners/DataConnectionListener';
import * as FeatureFlagListener from '../communicationChannelListeners/FeatureFlagListener';
import * as HelpServiceListener from '../communicationChannelListeners/HelpServiceListener';
import * as MetricDataPointsListener from '../communicationChannelListeners/MetricDataPointListeners';
import * as SearchEPTListner from '../communicationChannelListeners/SearchEPTListner';
import * as SessionManagementListener from '../communicationChannelListeners/SessionManagementListener';
import * as TelemetryListener from '../communicationChannelListeners/TelemetryListener';

enum MicroViewDebugModes { none, remote, local }

export const SearchMicroViewScriptClassName = 'SearchMicroViewScript';
export class SearchMicroViewHostService {
    private alreadyLoaded = false;
    private locale: string = undefined;
    private bundleReadyCommunicationChannelMessagePromise: Promise<void>;
    public static stopListeningToCommunicationChannels: () => void;

    private loadFileResolveFunction: () => void;
    private loadFileRejectFunction: () => void;

    public static instance = new SearchMicroViewHostService();
    public bundleReadyPromise: Promise<void>;

    constructor() {
        if (SearchMicroViewHostService.stopListeningToCommunicationChannels) {
            SearchMicroViewHostService.stopListeningToCommunicationChannels();
        }

        this.bundleReadyPromise = new Promise<void>((resolve, reject) => {
            this.loadFileResolveFunction = resolve;
            this.loadFileRejectFunction = reject;
        });

        this.bundleReadyPromise.then(() => {
        }, () => {
            if (this.getMicroViewDebugMode() !== MicroViewDebugModes.none) {
                // eslint-disable-next-line no-console
                console.error('Search microview JS files fail to load.');
            }
        });
    }

    private requestScripts(urls: string[], version: string): void {
        const promises: Promise<void>[] = urls
            .map(url => url + (version ? '?version=' + version : ''))
            .map(this.loadScript);

        Promise.all(promises).then(() => {
            this.bundleReadyCommunicationChannelMessagePromise.then(() => {
                this.loadFileResolveFunction();
            });
        }, () => {
            this.loadFileRejectFunction();
        });
    }

    public bootstrapMicroView() {
        const locale = this.getLocale();
        const mode = this.getMicroViewDebugMode();
        const version = this.getSearchMicroViewVersion(mode);

        if (!this.alreadyLoaded) {
            this.locale = locale;
            this.alreadyLoaded = true;

            // eslint-disable-next-line no-console
            console.log(`Search Result Page ${version}`);

            this.listenToCommunicationChannels();
            this.requestScripts(this.getUrls(mode, locale), version);
            this.setSearchMicroViewVersion(mode);
        } else if (this.locale !== locale) {
            this.locale = locale;
            this.requestScripts(this.getLocaleUrls(mode, locale), version);
        }
    }

    private listenToCommunicationChannels() {
        const stopListeningToFeatureFlag = FeatureFlagListener.startListening();
        const stopListeningToDataConnectionListener = DataConnectionListener.startListening();
        const stopListeningToMetricDataPoints = MetricDataPointsListener.startListening();
        const stopListeningToSessionManagement = SessionManagementListener.startListening();
        const stopListeningToTelemetry = TelemetryListener.startListening();
        const stopListeningToHelpService = HelpServiceListener.startListening();
        const stopListeningToSearchEPTListner = SearchEPTListner.startListening();

        let stopListeningToSearchMicroViewBundleReadyChannel: () => void;
        this.bundleReadyCommunicationChannelMessagePromise = new Promise<void>((resolve) => {
            stopListeningToSearchMicroViewBundleReadyChannel = (new SearchMicroViewBundleReadyChannel()).listen(() => {
                resolve();
            });
        });

        SearchMicroViewHostService.stopListeningToCommunicationChannels = () => {
            stopListeningToSearchMicroViewBundleReadyChannel();
            stopListeningToFeatureFlag();
            stopListeningToDataConnectionListener();
            stopListeningToMetricDataPoints();
            stopListeningToSessionManagement();
            stopListeningToTelemetry();
            stopListeningToHelpService();
            stopListeningToSearchEPTListner();
        };
    }

    private loadScript(url: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            let scriptTag = document.createElement('SCRIPT');
            scriptTag.setAttribute('src', url);
            scriptTag.className = SearchMicroViewScriptClassName;
            document.body.appendChild(scriptTag);
            scriptTag.onload = () => {
                resolve();
            };
            scriptTag.onerror = () => {
                reject();
            };
        });
    }

    private getUrls(mode: MicroViewDebugModes, locale: string): string[] {
        switch (mode) {
            case MicroViewDebugModes.none:
                return [
                    `/content-exploration/search-microview/search-dist/compiled-locales/messages.${locale}.js`,
                    `/content-exploration/search-microview/search-dist/compiled-locales/formatters-and-parsers.${locale}.js`,
                    '/content-exploration/search-microview/search-microview.min.js'
                ];
            case MicroViewDebugModes.remote:
                return [
                    `/content-exploration/search-microview/search-dist/compiled-locales/messages.${locale}.js`,
                    `/content-exploration/search-microview/search-dist/compiled-locales/formatters-and-parsers.${locale}.js`,
                    '/content-exploration/search-microview/search-microview.js'
                ];
            case MicroViewDebugModes.local:
                return [
                    `/search-dist/compiled-locales/messages.${locale}.js`,
                    `/search-dist/compiled-locales/formatters-and-parsers.${locale}.js`,
                    '/search-microview.js'
                ];
        }
    }

    private getLocaleUrls(mode: MicroViewDebugModes, locale: string): string[] {
        switch (mode) {
            case MicroViewDebugModes.none:
                return [
                    `/content-exploration/search-microview/search-dist/compiled-locales/messages.${locale}.js`,
                    `/content-exploration/search-microview/search-dist/compiled-locales/formatters-and-parsers.${locale}.js`
                ];
            case MicroViewDebugModes.remote:
                return [
                    `/content-exploration/search-microview/search-dist/compiled-locales/messages.${locale}.js`,
                    `/content-exploration/search-microview/search-dist/compiled-locales/formatters-and-parsers.${locale}.js`
                ];
            case MicroViewDebugModes.local:
                return [
                    `/search-dist/compiled-locales/messages.${locale}.js`,
                    `/search-dist/compiled-locales/formatters-and-parsers.${locale}.js`
                ];
        }
    }

    private getLocale(): string {
        const sessionManagementService: SessionManagementService = getSessionManagementServiceInstance();
        const language = sessionManagementService.sessionLanguage() ?? ServerSettingsUnauthenticated.serverLanguage();
        const localeResult = LocalizationLoader.selectLocaleOrDefault(language);
        return localeResult;
    }

    private getMicroViewDebugMode(): MicroViewDebugModes {
        return MicroViewDebugModes[queryString.parse(Location.instance.search())['microViewDebug'] as keyof typeof MicroViewDebugModes] || MicroViewDebugModes.none;
    }

    private async setSearchMicroViewVersion(mode: MicroViewDebugModes): Promise<void> {
        if (mode === MicroViewDebugModes.local) {
            return;
        }

        try {
            const timestamp = new Date().getTime().toString(36);
            const response = await axios.get(`/content-exploration/search-microview/version.txt?ts=${timestamp}`);
            localStorage.setItem('searchMicroviewVersion', response.data);
        } catch {
            return;
        }
    }

    private getSearchMicroViewVersion(mode: MicroViewDebugModes): string {
        if (mode === MicroViewDebugModes.local) {
            return '';
        }

        const version = localStorage.getItem('searchMicroviewVersion');

        return version || '';
    }
}